import { useContext } from "react";
import {
    createFailure,
    ensure,
    NamedProperty,
    validationFailed,
    ValidationResult,
    validationSuccessful
} from "sprint-common-web";

import { CreateEntityResponse } from "../../../../../../common/api/common/CreateEntityResponse";
import { CreateEpisodeRequest } from "../../../../api/CreateEpisodeRequest";
import { useAssetsManageTvSeriesApi } from "../../../../api/useAssetsManageTvSeriesApi";
import { EpisodeDetails } from "../../../../TvSeriesDetails";
import { SeasonPageContext } from "../../context/SeasonPageContext";
import { CreateEpisodeFormInputs } from "./CreateEpisodeFormInputs";

export const useCreateEpisodeForm = () => {
    const { selectedSeason, selectedTvSeries } = useContext(SeasonPageContext);
    const { createEpisode } = useAssetsManageTvSeriesApi();

    const createNewEpisode = async (inputs: CreateEpisodeFormInputs): Promise<EpisodeDetails> => {
        return createEpisode(
            createRequest(inputs, selectedSeason, {
                title: selectedTvSeries.title,
                id: selectedTvSeries.id
            })
        ).then((response: CreateEntityResponse) => {
            return createNewEpisodeDetails(response.entityId, inputs);
        });
    };

    const createNewEpisodeDetails = (newId: string, inputs: CreateEpisodeFormInputs): EpisodeDetails => {
        ensure(inputs.number);

        return {
            id: newId,
            title: inputs.title,
            number: inputs.number,
            versions: []
        };
    };

    const isNewEpisodeTitleUnique = (title: string): ValidationResult => {
        const isUnique = !selectedSeason.episodes.some(
            (episode: EpisodeDetails) => episode.title.toLowerCase() === title?.toLowerCase()
        );

        if (isUnique) {
            return validationSuccessful();
        }

        const failure = createFailure(
            "The title is already taken. Please enter a different title",
            "episode.TitleAlreadyTaken"
        );

        return validationFailed([failure]);
    };

    const isNewEpisodeNumberUnique = (number: number): ValidationResult => {
        const isUnique = !selectedSeason.episodes.some(
            (episode: EpisodeDetails) => episode.number === number && !episode.mergedTargetId
        );
        if (isUnique) {
            return validationSuccessful();
        }

        const failure = createFailure(
            "The episode number is already taken. Please enter a different number",
            "episode.NumberAlreadyTaken"
        );

        return validationFailed([failure]);
    };

    return {
        createNewEpisode,
        isNewEpisodeTitleUnique,
        isNewEpisodeNumberUnique
    };
};

const createRequest = (
    inputs: CreateEpisodeFormInputs,
    season: NamedProperty & { number: number },
    tvSeries: { title: string; id: string }
): CreateEpisodeRequest => {
    ensure(inputs.title);
    ensure(inputs.number);

    return {
        seasonId: season.id,
        seasonName: season.name,
        tvSeriesTitle: tvSeries.title,
        tvSeriesId: tvSeries.id,
        title: inputs.title,
        episodeNumber: inputs.number,
        seasonNumber: season.number
    };
};
